import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Packages  } from "assets/SideBarIcons/pakage.svg";
import StyledInput from "common/SearchField/Search";
import { useAssignPackagePriceListService, useGetPackagePriceListService, useUnassignPackagePriceListService } from 'subModule/src/services/packages/usePackages';
import PackageTabs from '../PackageTabs';
import { Grid } from '@mui/material';
import { PackagePricelistsTable } from './PackagePricelistsTable';
import Loader from 'layout/Loader';
import { useForm } from 'react-hook-form';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import { selectUserInfo } from 'features/user/userSlice';
import { useSelector } from 'react-redux';
import { GetPackageByIdResponse, PackagesResponse } from 'subModule/src/services/packages/interface';
import { PackagesProfileService } from 'subModule/src/core/services';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';

export default function PackagePricelistsListing() {
    const params = useParams();
    const packageId = params?.id;
    const navigate = useNavigate();
    const [packageTitle, setPackageTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [packages, setPackages] = useState<PackagesResponse>({});
    const { register, control, formState: { errors }, watch, setValue, trigger, reset } = useForm<FormData>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetPackagePriceListService({ pageNumber: pageNo, size: pageSize, title: searchTerm }, +(params as any).id) : useGetPackagePriceListService({ pageNumber: pageNo, size: pageSize }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const loggedInUserData = useSelector(selectUserInfo);
    const defaultPriceListId = loggedInUserData?.business?.priceListId || 0;
    const columns = [
        { id: 'pricelistId', label: '', numeric: false, disablePadding: false },
        { id: 'title', label: t('title'), numeric: false },
        { id: 'price', label: t('price'), numeric: false },
    ];
    const fetchData = useCallback(() => {
        reset();
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        if(Object.keys(packages)?.length > 0) {
            fetchData();
        }
    }, [pageNo, pageSize, searchTerm, packages]);
    useEffect(() => {
        setTimeout(getByID, 100);
    }, []);
    const getByID = () => {
        PackagesProfileService.getPackageById(+(params as any)?.id).then((res: GetPackageByIdResponse) => {
            if (res.success) {
                setPackages(res?.data);
                const name = pickProfileEntityTitle(res);
                if (name) setPackageTitle(name);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'packages', href: '/packages' },
            {
                labelKey: 'title',
                ...(packageTitle ? { label: packageTitle } : {}),
                href: packageId ? `/package/${packageId}` : undefined,
            },
            { labelKey: 'assignPriceList' },
        ],
        [packageId, packageTitle],
    );
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onUnassignPriceList({ packageId: +(params as any).id, pricelistId: row.id });
        } else {
            onAssignPriceList({ packageId: +(params as any).id, pricelistId: row.id, price: row.price });
        }
    }
    const { onAssignPriceList } = useAssignPackagePriceListService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('priceList') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignPriceList } = useUnassignPackagePriceListService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('priceList') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/packages')}
                heading={t('packages')}
                icon={<Packages height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <PackageTabs params={params} selectedIndex={5} formData={packages} /> : null}
                    <Box className='w-[100%]'>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7} lg={5} className='mt-3'>
                                <StyledInput
                                    fullWidth
                                    placeholder={t("title")}
                                    handleSearchClick={handleSearchClick}
                                    handleChange={handleInputChange}
                                    value={search}
                                    clearSearch={clearSearch}
                                />
                            </Grid>
                        </Grid>
                        {(isFetching || loading) ? <Loader /> : <PackagePricelistsTable
                            columns={columns}
                            rows={tableData?.pages[0]?.data?.content || []}
                            assignUnassignSingleEntity={assignUnassignSingleEntity}
                            form={{ register, control, formState: { errors }, watch, setValue, trigger, reset }}
                            assignPrivilege={checkPrivileges('ROLE_PACKAGE_PRICELIST_ASSIGN')}
                            unassignPrivilege={checkPrivileges('ROLE_PACKAGE_PRICELIST_UNASSIGN')}
                            {...{pageNo, pageSize, defaultPriceListId}}
                        />}
                    </Box>
                </div>
            </Box>
        </Box>
    )
}